import type { ErrorCode } from '@irace/shared';

/**
 * G-001 / §9, §11.1, §12 — the shape of one locally saved check-in.
 *
 * The record is deliberately the *only* durable state a participant has: the
 * server is not consulted before it is written (AGENTS.md invariant 2), so
 * anything the retry engine needs later must be snapshotted here at save
 * time. The one thing that must never appear is a Turnstile token
 * (invariant 3) — every retry fetches a fresh one.
 */

export const OUTBOX_SCHEMA_VERSION = 1;

/**
 * `pending`  — saved locally, waiting for a send attempt.
 * `sending`  — an attempt is in flight (or was, before the tab died).
 * `accepted` — the API answered 202; the Queue owns it now.
 * `failed`   — terminally rejected; retrying would fail the same way.
 */
export type OutboxStatus = 'pending' | 'sending' | 'accepted' | 'failed';

export interface OutboxData {
  fullName: string;
  phone?: string;
  email?: string;
  /** Extra configured fields, keyed by field key (§28.4). */
  payload?: Record<string, unknown>;
}

export interface OutboxRecord {
  /** §11.1 — bumped when this shape changes; old records migrate in place. */
  schemaVersion: number;
  submissionId: string;
  eventId: string;
  /**
   * The config version the form was rendered with. Sent verbatim and never
   * used as a gate (AGENTS.md invariant 6), which is exactly why a pending
   * record may safely outlive a config bump.
   */
  eventConfigVersion: number;
  locationId: string;
  /** Snapshotted so the pending screen can name the location without bootstrap. */
  locationName: string;
  data: OutboxData;
  clientCreatedAt: string;
  status: OutboxStatus;
  attemptCount: number;
  lastAttemptAt?: string;
  /** Why the last attempt did not end in 202. Never contains a token. */
  lastErrorCode?: ErrorCode | 'NETWORK' | 'TURNSTILE_CLIENT' | 'UNKNOWN_REJECTED';
}

/** Records the retry engine is allowed to pick up. */
export function isRetryable(record: OutboxRecord): boolean {
  return record.status === 'pending' || record.status === 'sending';
}

/**
 * Which server answers leave the record worth retrying.
 *
 * 403 is retryable on purpose: the *token* failed, not the submission, so a
 * fresh challenge with the same submission_id is the documented recovery
 * (§16). 400/409/413 are not — the same bytes would be rejected forever, so
 * the participant has to change something.
 *
 * Shared with the check-out path, which is why `CHECKOUT_NOT_ENABLED` is
 * listed: an event that does not accept departures will not start accepting
 * them because a device asked twice, so retrying would only burn battery.
 *
 * `UNKNOWN_REJECTED` is the client's stand-in for a 4xx whose code this bundle
 * predates (see `readErrorEnvelope` in api/client.ts). It is terminal because
 * the status class said so, and the status class is the half of that answer
 * that cannot go stale.
 */
export function isRetryableErrorCode(code: OutboxRecord['lastErrorCode']): boolean {
  switch (code) {
    case 'INVALID_REQUEST':
    case 'REQUEST_TOO_LARGE':
    case 'EVENT_NOT_ACTIVE':
    case 'INVALID_LOCATION':
    case 'EVENT_NOT_FOUND':
    case 'CHECKOUT_NOT_ENABLED':
    case 'UNKNOWN_REJECTED':
      return false;
    default:
      return true;
  }
}

/**
 * §11.1 — read a record written by an older bundle.
 *
 * A Service Worker update must never orphan pending data, so unknown-but-older
 * records are upgraded field by field rather than dropped. A record from a
 * *newer* schema (user went back to a cached older bundle) is left untouched:
 * guessing at fields we do not know about would corrupt it.
 */
export function migrateRecord(stored: unknown): OutboxRecord | null {
  if (typeof stored !== 'object' || stored === null) return null;
  const record = stored as Partial<OutboxRecord>;

  if (
    typeof record.submissionId !== 'string' ||
    typeof record.eventId !== 'string' ||
    typeof record.locationId !== 'string' ||
    typeof record.clientCreatedAt !== 'string' ||
    typeof record.data !== 'object' ||
    record.data === null ||
    typeof record.data.fullName !== 'string'
  ) {
    return null;
  }

  return {
    ...record,
    schemaVersion:
      typeof record.schemaVersion === 'number' ? record.schemaVersion : OUTBOX_SCHEMA_VERSION,
    submissionId: record.submissionId,
    eventId: record.eventId,
    eventConfigVersion:
      typeof record.eventConfigVersion === 'number' && record.eventConfigVersion >= 1
        ? record.eventConfigVersion
        : 1,
    locationId: record.locationId,
    locationName: typeof record.locationName === 'string' ? record.locationName : '',
    data: record.data,
    clientCreatedAt: record.clientCreatedAt,
    status: record.status ?? 'pending',
    attemptCount: typeof record.attemptCount === 'number' ? record.attemptCount : 0,
  };
}
